import type { JSX } from 'react';
import { merge, omit, uniq } from 'lodash';
import type { CustomControlCode } from '@common/enums';
import type { InputField, Section } from '@common/formEngine/formEngine.types';
import { getRegistryEntry } from '@common/workflow/serviceActivityRegistration';
import { getInitialData } from '@frontend/components/pages/episodeCategory/customControls/customControl';
import { PatientEpisodeCategoryContext } from '@frontend/components/pages/episodeCategory/patientEpisodeCategoryContext';
import { useProcessActivityContext, useProcessActivityEditContext } from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { useDefinedContext } from '@frontend/utils/hooks/useDefinedContext';
import type { EditFormCommonProps } from './editForm.types';
import { EditFormContextProvider } from './EditFormContextProvider';
import { EditFormSection } from './EditFormSection';
import { getChangedData } from './editFormSectionFieldUtils';
import { shouldIncludeSection } from './editFormUtils';
import { getHookData } from './hookUtils';

type EditFormProps<
    Data extends Record<string, unknown>,
    ContentKey extends string | undefined,
    TCustomControlCode extends CustomControlCode | undefined,
    Version extends number,
> = EditFormCommonProps<Data, ContentKey> & {
    readonly sections: Section<Data, ContentKey, TCustomControlCode, Version>[];
};

export function EditForm<
    Data extends Record<string, unknown>,
    ContentKey extends string | undefined,
    TCustomControlCode extends CustomControlCode | undefined,
    Version extends number,
>({
    contentComponents,
    inputComponents,
    sections,
}: EditFormProps<Data, ContentKey, TCustomControlCode, Version>): JSX.Element {
    const abortSignal = useAbortSignal();
    const { patient } = useDefinedContext(PatientEpisodeCategoryContext);
    const { customControlCode, customControlVersion, serviceActivity } = useProcessActivityContext();
    const { onChange, processActivityContextData, setHookData, setIsAutoSaveEnabled } = useProcessActivityEditContext<
        Data,
        TCustomControlCode,
        Version
    >();

    return (
        <EditFormContextProvider onChange={handleChange}>
            {sections
                .filter((section) => shouldIncludeSection(section, processActivityContextData))
                .map((section) => (
                    <EditFormSection
                        contentComponents={contentComponents}
                        inputComponents={inputComponents}
                        key={
                            (section.fields.length && section.fields.map((field) => field.key).join('-')) ||
                            section.subsections
                                ?.flatMap((subsection) => subsection.fields.map((field) => field.key))
                                .join('-')
                        }
                        section={section}
                    />
                ))}
        </EditFormContextProvider>
    );

    async function handleChange(
        value: unknown,
        changedField: InputField<Data, TCustomControlCode, Version>
    ): Promise<void> {
        const initialData = getInitialData<Data>(customControlCode, customControlVersion);
        const previousValue = processActivityContextData[changedField.key];
        let { changedData, validateKeys } = getChangedData({
            changedField,
            initialData,
            previousValue,
            processActivityContextData,
            sections,
            value,
        });

        onChange(changedData, validateKeys);
        const { hooks } = getRegistryEntry(customControlCode, customControlVersion);
        if (hooks) {
            setIsAutoSaveEnabled(false);
            const changedProcessActivityContextData = merge(processActivityContextData, changedData);
            const hookData = await getHookData({
                abortSignal,
                changedData,
                customControlCode,
                customControlVersion,
                hooks,
                patientId: patient.id,
                processActivityContextData: changedProcessActivityContextData,
            });

            const hookChangedData = getChangedData({
                changedField,
                hookData,
                initialData,
                previousValue,
                processActivityContextData: changedProcessActivityContextData,
                sections,
                value,
            });
            changedData = merge(changedData, hookChangedData.changedData);
            // Omit the changed field from the changed data, because it has already been changed above
            changedData = omit<Partial<Data>>(changedData, changedField.key);
            validateKeys = uniq([...validateKeys, ...hookChangedData.validateKeys]);
            setIsAutoSaveEnabled(serviceActivity.isAutoSaveEnabled);
            setHookData(hookData);

            /*
             * Some input types are affected by "cursor jumping" when the value changes, which is caused by `async`
             * functions in combination with `setState`, see https://giacomocerquone.com/blog/keep-input-cursor-still/.
             * This can be resolved by calling `setState` twice, once before `await` and once after.
             * React understands that these two `setState` calls are related and will prevent the cursor from jumping.
             */
            onChange(changedData, validateKeys);
        }
    }
}
